import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid2';
import Button from 'components/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import IssueDetailsLoader from 'views/IssueDetails/components/Loaders/IssueDetailsLoader';
import AILoader from 'views/IssueDetails/components/Loaders/AILoader';
import Info from 'views/IssueDetails/components/Info';
import { IssueDetailsContainer } from 'views/IssueDetails/components/AIAnalysis/Styles';
import { useAIAnalytics } from 'views/IssueDetails/components/AIAnalysis/hooks/useAIAnalytics';

/**
 * NIDC Inbound Layout for AI file descriptions
 * This layout uses a single column without left/right split
 * and displays the source data directly
 */
const NIDCInboundLayout = ({
  analyticData,
  fetchData,
  fetchStatus,
  issue,
  rerunDataAnalysis,
}) => {
  const { isLoading, isInProgress } = useAIAnalytics({
    analyticData,
    fetchData,
    fetchStatus,
    rerunDataAnalysis,
  });

  // Extract medical report data from analytics
  const getMedicalReportData = () => {
    try {
      const result = analyticData?.data?.issueAnalytics?.result;

      // First try to get from structured data (preferred)
      if (result?.resultMetaData?.structuredData) {
        return result.resultMetaData.structuredData;
      }

      // Fallback: parse from answer field
      if (result?.answer) {
        // Try parsing JSON wrapped in markdown code blocks
        const jsonMatch = result.answer.match(/```json\n([\s\S]*?)\n```/);
        if (jsonMatch) {
          return JSON.parse(jsonMatch[1]);
        }

        // Try parsing as plain JSON string
        return JSON.parse(result.answer);
      }

      return null;
    } catch (error) {
      console.error('Error parsing medical report data:', error);
      return null;
    }
  };

  const medicalData = getMedicalReportData();

  // If loading or in progress, show loading state
  if (isLoading || isInProgress) {
    return (
      <Fragment>
        <IssueDetailsContainer container>
          <Grid
            size={12}
            style={{ position: 'relative', marginTop: 20, marginBottom: 20 }}>
            <AILoader />
            <IssueDetailsLoader />
            <IssueDetailsLoader />
          </Grid>
        </IssueDetailsContainer>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <IssueDetailsContainer
        container
        sx={{
          maxHeight: '85vh',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}>
        <Grid size={12}>
          <Info issue={issue} />
          <Divider />

          {/* Header Section */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              px: 2,
              py: 1,
              backgroundColor: '#f8f9fa',
              borderRadius: 1,
              border: '1px solid #e9ecef',
            }}>
            <Typography
              variant="h5"
              component="h1"
              sx={{ color: '#2c5282', fontWeight: 'bold' }}>
              医療レポート分析 - Patient Medical Summary
            </Typography>
            <Button
              variant="primary"
              onClick={rerunDataAnalysis}
              icon={<RefreshIcon />}
              disabled={isInProgress}
              style={{ marginLeft: '16px' }}>
              再分析実行
            </Button>
          </Box>

          {/* Medical Report Content */}
          <Box sx={{ px: 2 }}>
            {medicalData ? (
              <Box sx={{ mb: 4 }}>
                {Object.entries(medicalData).map(([key, section]) => {
                  if (!section || !section.title_ja || !section.content)
                    return null;

                  return (
                    <Paper
                      key={key}
                      elevation={2}
                      sx={{
                        mb: 3,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}>
                      {/* Section Header */}
                      <Box
                        sx={{
                          backgroundColor: '#e3f2fd',
                          color: '#1565c0',
                          px: 3,
                          py: 2,
                          borderBottom: '1px solid #e0e0e0',
                        }}>
                        <Typography
                          variant="h6"
                          component="h2"
                          sx={{ fontWeight: 'bold' }}>
                          {section.title_ja}
                        </Typography>
                      </Box>

                      {/* Section Content */}
                      <Box sx={{ px: 3, py: 3 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            lineHeight: 1.8,
                            fontSize: '15px',
                            color: '#333',
                            whiteSpace: 'pre-line',
                          }}>
                          {section.content || '記載なし'}
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            ) : (
              <Paper
                elevation={1}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  backgroundColor: '#f5f5f5',
                  border: '1px dashed #ccc',
                }}>
                <Typography variant="h6" sx={{ color: '#666', mb: 2 }}>
                  医療データが利用できません
                </Typography>
                <Typography variant="body2" sx={{ color: '#888' }}>
                  分析データを取得できませんでした。再分析を実行してください。
                </Typography>
              </Paper>
            )}
          </Box>
        </Grid>
      </IssueDetailsContainer>
    </Fragment>
  );
};

NIDCInboundLayout.propTypes = {
  analyticData: PropTypes.object.isRequired,
  fetchData: PropTypes.func.isRequired,
  issue: PropTypes.object.isRequired,
  fetchStatus: PropTypes.func.isRequired,
  rerunDataAnalysis: PropTypes.func.isRequired,
};

export default NIDCInboundLayout;
